// Data wygenerowania — JEDEN format, cztery postaci (język minimal, reguła 31).
//
// Domi, 20.09: „możemy dodać datę, o której został wygenerowany”. Data stoi w czterech
// miejscach naraz (wiersz serii, linia stanu strony serii, podpis kafla we „Wszystkich
// editach”, górna linia okna podglądu), więc format musi być jeden — inaczej ta sama
// chwila czyta się na liście jako „dziś 14:20”, a w oknie jako „20 wrz 2026, 14:20”.
//
//   dziś 14:20   ·   wczoraj 18:00   ·   18 wrz, 18:00   ·   18.09.2025
//
// „Dziś” i „wczoraj” liczy się KALENDARZOWO w strefie przeglądarki, nie po różnicy
// godzin: render o 23:50 jest „wczoraj” o 00:10, a nie „dziś” przez kolejne 23 godziny.
// Inny rok schodzi na zapis liczbowy — to ten sam próg, co „starsze niż rok” z briefu
// (data starsza niż 365 dni zawsze wpada w inny rok kalendarzowy), a przy tym nie
// zmienia postaci w nocy z 31 grudnia.
//
// Nazwa miesiąca i godzina idą przez `Intl` z aktywnym locale (`intlLocale`), więc
// angielski czytelnik dostaje „18 Sep, 18:00”, a nie polski skrót w angielskim zdaniu —
// to ta sama pułapka, którą opisuje `format.ts`.

import { intlLocale, type FormatLocale } from './format'

export interface GeneratedAtWords {
  /** „dziś {time}” */
  today: string
  /** „wczoraj {time}” */
  yesterday: string
}

/** Kalendarzowa różnica dni między dwiema chwilami w strefie przeglądarki. */
function daysApart(a: Date, b: Date): number {
  const day = (d: Date) => Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 86_400_000)
  return day(b) - day(a)
}

/**
 * Data i godzina wygenerowania, albo `null`, gdy jej nie ma.
 *
 * `null` jest odpowiedzią, nie awarią: edit bez pliku (w kolejce) NIE MA jeszcze daty
 * wygenerowania (rozstrzygnięcie 5), a ekran ma wtedy nie rysować myślnika.
 */
export function generatedAt(
  locale: FormatLocale,
  iso: string | null | undefined,
  words: GeneratedAtWords,
  now: Date = new Date(),
): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const tag = intlLocale(locale)
  const time = d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit', hour12: false })
  const apart = daysApart(d, now)
  if (apart === 0) return words.today.replace('{time}', time)
  if (apart === 1) return words.yesterday.replace('{time}', time)
  if (d.getFullYear() !== now.getFullYear()) {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
  }
  return `${d.toLocaleDateString(tag, { day: 'numeric', month: 'short' })}, ${time}`
}

export default generatedAt
